import React, { useState, useEffect } from 'react';
import './ChecklistGrading.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaClipboardCheck,
  FaUsers,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
  FaCommentAlt,
  FaFilter,
  FaTimes,
} from 'react-icons/fa';

const ChecklistGrading = ({ user, assignedCourse, myStudentIds }) => {
  const [students, setStudents] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [evaluationData, setEvaluationData] = useState(null);
  const [scores, setScores] = useState({});
  const [overallComments, setOverallComments] = useState('');
  const [itemComments, setItemComments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [evaluationStatus, setEvaluationStatus] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});
  const [viewMode, setViewMode] = useState('select'); // 'select', 'evaluate', 'view'
  const [filterStatus, setFilterStatus] = useState('all');
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [selectedBatch, setSelectedBatch] = useState('');

  const getValidBatches = (studentList) => {
    const minStartYear = new Date().getFullYear() - 4;
    return [...new Set(studentList.map(s => s.batch).filter(Boolean))]
      .filter(b => {
        const startYear = parseInt(b.split('-')[0]);
        return !isNaN(startYear) && startYear >= minStartYear;
      })
      .sort();
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (assignedCourse) {
      fetchStudents();
      fetchChecklists();
    }
  }, [assignedCourse]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/checklist-evaluations/course/${assignedCourse._id}/students`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      const data = await response.json();
      if (data.success) {
        setStudents(data.students);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/checklist-evaluations/course/${assignedCourse._id}/checklists`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch checklists');
      }

      const data = await response.json();
      if (data.success) {
        setChecklists(data.checklists);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
      setError('Failed to load checklists');
    }
  };

  const fetchEvaluationStatus = async (checklistId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/checklist-evaluations/course/${assignedCourse._id}/checklist/${checklistId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch evaluation status');
      }

      const data = await response.json();
      if (data.success) {
        setEvaluationStatus(data.statusData);
      }
    } catch (error) {
      console.error('Error fetching evaluation status:', error);
    }
  };

  const loadEvaluation = async () => {
    if (!selectedStudent || !selectedChecklist) {
      setError('Please select both a student and a checklist');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${collegeConfig.api.baseURL}/checklist-evaluations/course/${assignedCourse._id}/checklist/${selectedChecklist.checklistId}/student/${selectedStudent._id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load evaluation');
      }

      const data = await response.json();
      if (data.success) {
        setEvaluationData(data);
        
        if (data.isNew) {
          // Initialize scores for new evaluation
          const initialScores = {};
          data.checklist.observationItems.forEach(item => {
            initialScores[item.id] = 0;
          });
          setScores(initialScores);
          setItemComments({});
          setOverallComments('');
        } else {
          // Load existing evaluation data
          const existingScores = {};
          const existingComments = {};
          
          data.evaluation.scores.forEach(score => {
            existingScores[score.itemId] = score.score;
          });
          
          data.evaluation.itemComments?.forEach(comment => {
            existingComments[comment.itemId] = comment.comment;
          });
          
          setScores(existingScores);
          setItemComments(existingComments);
          setOverallComments(data.evaluation.overallComments || '');
        }
        
        setViewMode('evaluate');
        
        // Scroll to top of page when evaluation form opens
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      console.error('Error loading evaluation:', error);
      setError('Failed to load evaluation data');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (itemId, value) => {
    const maxScore = evaluationData?.checklist?.maxScore || 4;
    const numValue = Math.max(0, Math.min(maxScore, parseInt(value) || 0));
    setScores(prev => ({
      ...prev,
      [itemId]: numValue
    }));
  };

  const handleItemCommentChange = (itemId, comment) => {
    setItemComments(prev => ({
      ...prev,
      [itemId]: comment
    }));
  };

  const calculateTotalScore = () => {
    return Object.values(scores).reduce((sum, score) => sum + (score || 0), 0);
  };

  const calculatePercentage = () => {
    if (!evaluationData) return 0;
    const maxPossible = evaluationData.checklist.observationItems.length * evaluationData.checklist.maxScore;
    return maxPossible > 0 ? ((calculateTotalScore() / maxPossible) * 100).toFixed(2) : 0;
  };

  const saveEvaluation = async (status = 'draft') => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const scoresArray = evaluationData.checklist.observationItems.map(item => ({
        itemId: item.id,
        itemName: item.name,
        score: scores[item.id] || 0
      }));
      
      const itemCommentsArray = Object.entries(itemComments)
        .filter(([_, comment]) => comment)
        .map(([itemId, comment]) => ({ itemId, comment }));
      
      const courseInfo = selectedStudent.assignedCourses?.find(
        ac => ac.courseId === assignedCourse._id
      );
      
      const payload = {
        courseId: assignedCourse._id,
        checklistId: selectedChecklist.checklistId,
        studentId: selectedStudent._id,
        scores: scoresArray,
        overallComments,
        itemComments: itemCommentsArray,
        status,
        academicYear,
        batch: courseInfo?.batch || ''
      };
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/checklist-evaluations/evaluate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to save evaluation');
      }
      
      const data = await response.json();
      if (data.success) {
        setSuccess(status === 'submitted' ? 'Evaluation submitted successfully!' : 'Evaluation saved as draft');
        setTimeout(() => setSuccess(''), 3000);
        
        if (status === 'submitted') {
          // Reset and go back to selection view
          setViewMode('select');
          setSelectedStudent(null);
          setSelectedChecklist(null);
          setEvaluationData(null);
          fetchEvaluationStatus(selectedChecklist.checklistId);
          
          // Scroll to top when returning to selection view
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error('Error saving evaluation:', error);
      setError('Failed to save evaluation');
    } finally {
      setLoading(false);
    }
  };
  
  const toggleItemExpansion = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };
  
  const handleChecklistSelect = (checklist) => {
    setSelectedChecklist(checklist);
    fetchEvaluationStatus(checklist.checklistId);
  };
  
  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };
  
  const resetSelection = () => {
    setViewMode('select');
    setSelectedStudent(null);
    setSelectedChecklist(null);
    setEvaluationData(null);
    setScores({});
    setItemComments({});
    setOverallComments('');
    
    // Scroll to top when closing evaluation form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const batchStudentIds = new Set(
    selectedBatch ? students.filter(s => s.batch === selectedBatch).map(s => s._id) : []
  );

  const filteredStudents = students.filter(student => {
    if (!selectedBatch || student.batch !== selectedBatch) return false;
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMyStudents = !myStudentIds || myStudentIds.length === 0 || myStudentIds.includes(student._id);
    return matchesSearch && matchesMyStudents;
  });

  const filteredEvaluationStatus = evaluationStatus.filter(item => {
    if (!selectedBatch || !batchStudentIds.has(item.studentId)) return false;

    // Apply My Students filter
    const matchesMyStudents = !myStudentIds || myStudentIds.length === 0 || myStudentIds.includes(item.studentId);
    if (!matchesMyStudents) return false;

    // Apply search filter
    const matchesSearch = searchTerm === '' ||
      item.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    // Apply status filter
    if (filterStatus === 'all') return true;
    if (filterStatus === 'completed') return item.evaluation && item.evaluation.status === 'submitted';
    if (filterStatus === 'pending') return !item.evaluation || item.evaluation.status === 'draft';
    return true;
  });
  
  const getGradeColor = (percentage) => {
    if (percentage >= 90) return '#2ECC71';
    if (percentage >= 80) return '#3498DB';
    if (percentage >= 70) return '#F39C12';
    if (percentage >= 60) return '#E67E22';
    if (percentage >= 50) return '#E74C3C';
    return '#C0392B';
  };
  
  if (!assignedCourse) {
    return (
      <div className="no-course-message44">
        <FaExclamationTriangle />
        <p>No course assigned. Please contact administrator.</p>
      </div>
    );
  }
  
  return (
    <div className="checklist-grading-container44">
      <div className="grading-header44">
        <h2>Checklist Grading</h2>
        <p>Evaluate students using course checklists</p>
      </div>
      
      {success && <div className="alert44 alert-success44">{success}</div>}
      {error && <div className="alert44 alert-error44">{error}</div>}
      
      {viewMode === 'select' && (
        <>
          {/* Checklist Selection */}
          <div className="selection-section44">
            <h3><FaClipboardCheck /> Select Checklist</h3>
            <div className="checklists-grid44">
              {checklists.length === 0 ? (
                <div className="no-checklists44">
                  <p>No checklists available for this course</p>
                </div>
              ) : (
                checklists.map(checklist => (
                  <div 
                    key={checklist.checklistId}
                    className={`checklist-card44 ${selectedChecklist?.checklistId === checklist.checklistId ? 'selected44' : ''}`}
                    onClick={() => handleChecklistSelect(checklist)}
                  >
                    <div className="checklist-card-header44">
                      <h4>{checklist.name}</h4>
                      {selectedChecklist?.checklistId === checklist.checklistId && (
                        <FaCheckCircle className="selected-icon44" />
                      )}
                    </div>
                    <div className="checklist-info44">
                      <p><strong>Items:</strong> {checklist.observationItems.length}</p>
                      <p><strong>Max Score per Item:</strong> {checklist.maxScore}</p>
                      <p><strong>Total Points:</strong> {checklist.observationItems.length * checklist.maxScore}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          
          {/* Student Selection */}
          {selectedChecklist && (
            <div className="selection-section44">
              <h3><FaUsers /> Select Student</h3>

              <div className="search-filter-bar44">
                <div className="filter-box44">
                  <FaFilter />
                  <select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                  >
                    <option value="">Select Batch</option>
                    {getValidBatches(students).map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                <div className="search-box44">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={!selectedBatch}
                  />
                </div>

                <div className="filter-box44">
                  <FaFilter />
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    disabled={!selectedBatch}
                  >
                    <option value="all">All Students</option>
                    <option value="completed">Evaluated</option>
                    <option value="pending">Not Evaluated</option>
                  </select>
                </div>
              </div>

              {!selectedBatch && (
                <div style={{ textAlign: 'center', padding: '24px', color: '#888', background: '#f8f9fa', borderRadius: 8, margin: '12px 0' }}>
                  Please select a batch to view students.
                </div>
              )}

              {selectedBatch && <div className="students-list44">
                {filteredEvaluationStatus.length > 0 ? (
                  filteredEvaluationStatus.map(item => (
                    <div 
                      key={item.studentId}
                      className={`student-item44 ${selectedStudent?._id === item.studentId ? 'selected44' : ''}`}
                      onClick={() => {
                        const student = students.find(s => s._id === item.studentId);
                        handleStudentSelect(student);
                      }}
                    >
                      <div className="student-info44">
                        <h4>{item.studentName}</h4>
                        <p>{item.registrationNumber}</p>
                      </div>
                      <div className="evaluation-status44">
                        {item.evaluation ? (
                          <div className="status-badge44 completed44">
                            <FaCheckCircle />
                            <span>{item.evaluation.grade} ({item.evaluation.percentageScore.toFixed(1)}%)</span>
                          </div>
                        ) : (
                          <div className="status-badge44 pending44">
                            <FaTimesCircle />
                            <span>Not Evaluated</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  filteredStudents.map(student => (
                    <div 
                      key={student._id}
                      className={`student-item44 ${selectedStudent?._id === student._id ? 'selected44' : ''}`}
                      onClick={() => handleStudentSelect(student)}
                    >
                      <div className="student-info44">
                        <h4>{student.name}</h4>
                        <p>{student.registrationNumber}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>}

              {selectedStudent && selectedChecklist && (
                <div className="action-buttons44">
                  <button 
                    className="btn-primary44"
                    onClick={loadEvaluation}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Start Evaluation'}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      {viewMode === 'evaluate' && evaluationData && (
        <div className="evaluation-form44">
          <div className="evaluation-header44">
            <div className="eval-info44">
              <h3>Evaluating: {evaluationData.student?.name || selectedStudent?.name}</h3>
              <p>Checklist: {evaluationData.checklist.name}</p>
              <p>Registration: {evaluationData.student?.registrationNumber || selectedStudent?.registrationNumber}</p>
            </div>
            <button className="btn-close44" onClick={resetSelection}>
              <FaTimes /> Close
            </button>
          </div>
          
          <div className="evaluation-metadata44">
            <div className="form-group44">
              <label>Academic Year</label>
              <input 
                type="text"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                placeholder="e.g., 2024-2025"
              />
            </div>
          </div>
          
          <div className="observation-items44">
            <h4>Observation Items</h4>
            {evaluationData.checklist.observationItems.map((item, index) => (
              <div key={item.id} className="observation-item-card44">
                <div className="item-header44">
                  <div className="item-info44">
                    <span className="item-number44">{index + 1}.</span>
                    <span className="item-name44">{item.name}</span>
                  </div>
                  <div className="score-input44">
                    <label>Score (0-{evaluationData.checklist.maxScore}):</label>
                    <input 
                      type="number"
                      min="0"
                      max={evaluationData.checklist.maxScore}
                      value={scores[item.id] || 0}
                      onChange={(e) => handleScoreChange(item.id, e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="item-comment-section44">
                  <button 
                    className="toggle-comment44"
                    onClick={() => toggleItemExpansion(item.id)}
                  >
                    <FaCommentAlt /> Add Comment
                    {expandedItems[item.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  
                  {expandedItems[item.id] && (
                    <textarea
                      placeholder="Optional comments for this item..."
                      value={itemComments[item.id] || ''}
                      onChange={(e) => handleItemCommentChange(item.id, e.target.value)}
                      rows="3"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="overall-section44">
            <div className="score-summary44">
              <h4>Score Summary</h4>
              <div className="summary-stats44">
                <div className="stat44">
                  <label>Total Score:</label>
                  <span className="value44">{calculateTotalScore()} / {evaluationData.maxPossibleScore || (evaluationData.checklist.observationItems.length * evaluationData.checklist.maxScore)}</span>
                </div>
                <div className="stat44">
                  <label>Percentage:</label>
                  <span 
                    className="value44"
                    style={{ color: getGradeColor(calculatePercentage()) }}
                  >
                    {calculatePercentage()}%
                  </span>
                </div>
              </div>
            </div>
            
            <div className="overall-comments44">
              <h4>Overall Comments</h4>
              <textarea
                placeholder="Add overall feedback and comments..."
                value={overallComments}
                onChange={(e) => setOverallComments(e.target.value)}
                rows="4"
              />
            </div>
          </div>
          
          <div className="form-actions44">
            <button 
              className="btn-primary44"
              onClick={() => saveEvaluation('submitted')}
              disabled={loading}
            >
              <FaCheckCircle /> Submit Evaluation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistGrading;